import { useMemo } from "react";
import { toast } from "react-toastify";

import _debounce from "lodash/debounce";

import ColorPicker from "components/base/ColorPicker";
import { useProjectContext } from "components/project/projects/context";
import { useSelectedProjectPermissions } from "components/project/projects/hooks";
import { useUpdateProjectLinkedShapefile } from "components/project/shapefiles/hooks";
import {
  INVALID_FILL_PATTERN_MESSAGE,
  INVALID_LINE_STYLE_MESSAGE,
  ShapefileStyleProp,
  isFillPattern,
  isLineStyle
} from "components/project/shapefiles/shapefiles.constants";

interface IProjectLinkedShapefileColorPickerProps {
  projectShapefileId: string;
  shapefileId: string;
  color: string;
  strokeColor: string;
  thickness: number;
  opacity: number;
  disabled?: boolean;
  fileType?: string;
  lineStyle?: string;
  fillPattern?: string;
}

export function ProjectLinkedShapefileColorPicker(
  props: IProjectLinkedShapefileColorPickerProps
) {
  const {
    projectShapefileId,
    shapefileId,
    color,
    strokeColor,
    thickness,
    opacity,
    disabled,
    fileType,
    lineStyle,
    fillPattern
  } = props;

  const { selectedProjectId } = useProjectContext();

  const projectPermissions = useSelectedProjectPermissions();

  const { updateProjectLinkedShapefile } = useUpdateProjectLinkedShapefile();

  // TODO BF: repeated code
  const debouncedUpdateProjectLinkedShapefileProperty = useMemo(
    () =>
      _debounce(
        (key, value, projectShapefileId) =>
          updateProjectLinkedShapefile.mutate({
            body: {
              key,
              value
            },
            projectId: selectedProjectId,
            projectShapefileId: projectShapefileId,
            shapefileId: shapefileId
          }),
        500
      ),
    [selectedProjectId]
  );

  const updateOpacity = (key, value) =>
    debouncedUpdateProjectLinkedShapefileProperty("opacity", value, projectShapefileId);

  const updateThickness = (key, value) =>
    debouncedUpdateProjectLinkedShapefileProperty("thickness", value, projectShapefileId);

  const updateColor = (key, value) => {
    if (key === ShapefileStyleProp.LineStyle && !isLineStyle(value)) {
      toast.error(INVALID_LINE_STYLE_MESSAGE);
      return;
    }

    if (key === ShapefileStyleProp.FillPattern && !isFillPattern(value)) {
      toast.error(INVALID_FILL_PATTERN_MESSAGE);
      return;
    }

    updateProjectLinkedShapefile.mutate({
      body: {
        key,
        value
      },
      projectId: selectedProjectId,
      projectShapefileId: projectShapefileId,
      shapefileId: shapefileId
    });
  };

  return (
    <ColorPicker
      onChange={updateColor}
      onChangeOpacity={updateOpacity}
      onChangeThickness={updateThickness}
      onChangeLineStyle={updateColor}
      onChangeFillPattern={updateColor}
      values={{
        color: color,
        strokeColor: strokeColor,
        ...(lineStyle !== undefined && { lineStyle: lineStyle ?? "solid" }),
        ...(fillPattern !== undefined && { fillPattern: fillPattern ?? "none" }),
        thickness: thickness,
        opacity: (opacity ?? 1) * 100
      }}
      isReadOnly={disabled || !projectPermissions.canEditShapefiles}
      isAtShapefileLevel={true}
      isGeoTIFF={fileType === "GeoTiff"}
    />
  );
}
